import { toaster } from "evergreen-ui";
import { useEffect, useRef, useState } from "react";
import {
    TundraStorefrontCreateCustomDomainInput,
    TundraStorefrontValidateCustomDomainInput
} from "../../../../common/inputs/TundraStorefrontInput";
import { TundraStorefront } from "../../../../common/models";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import {
    deleteTundraStorefront,
    useCreateCustomDomainMutation,
    useCreateStorefrontMutation,
    useGetStorefrontQuery,
    useValidateCustomDomainMutation
} from "../../datastore/TundraStorefrontDataStore";
import { StoreTundraAdminCreateView } from "./StoreTundraAdminCreateView";
import { StoreTundraAdminGetView } from "./StoreTundraAdminGetView";
import { useStoreEditDetailsContext } from "./StoreEditDetailsContext";

export const resolveRenderedStorefront = (
    hasPolled: boolean,
    fetched: TundraStorefront | null,
    validated: TundraStorefront | null | undefined,
    created: TundraStorefront | null | undefined
): TundraStorefront | null => {
    return (hasPolled ? fetched : null) ?? validated ?? created ?? fetched;
};

export const handlePollTick = (
    inFlightRef: { current: boolean },
    refetch: () => Promise<boolean>,
    markPolled: () => void
): void => {
    if (inFlightRef.current) {
        return;
    }
    inFlightRef.current = true;
    refetch()
        .then((succeeded) => {
            if (succeeded) {
                markPolled();
            }
        })
        .finally(() => {
            inFlightRef.current = false;
        });
};

// The flag means a second validate run completes work tundra-service started.
export const shouldAutoRevalidate = (storefront: Pick<TundraStorefront, "requiresRevalidation"> | null | undefined): boolean => {
    return storefront?.requiresRevalidation === true;
};

export const StoreTundraAdminPage = () => {

    const {
        store
    } = useStoreEditDetailsContext();

    const {
        data: getStorefrontData,
        loading: getStorefrontLoading,
        refetch: refetchStorefront
    } = useGetStorefrontQuery(store?.domain ?? "");

    const [
        createCustomDomain,
        {
            loading: createCustomDomainLoading,
            called: createCustomDomainCalled,
            data: createdCustomDomainData
        }
    ] = useCreateCustomDomainMutation();

    const [
        validateCustomDomain,
        {
            loading: validateCustomDomainLoading,
            data: validatedCustomDomainData
        }
    ] = useValidateCustomDomainMutation();

    const [
        createStorefront,
        {
            loading: createStorefrontLoading,
            called: createStorefrontCalled,
            data: createdStorefrontData
        }
    ] = useCreateStorefrontMutation();

    const [
        deletingStorefront,
        setDeletingStorefront
    ] = useState<boolean>(false);

    // The validate mutation exposes one loading flag for the whole page, so the card needs to
    // know which domain it belongs to or every card renders as processing.
    const [validatingCustomDomain, setValidatingCustomDomain] = useState<string | null>(null);
    const autoRevalidatedRef = useRef<string | null>(null);
    const requestValidateCustomDomain = (input: TundraStorefrontValidateCustomDomainInput): void => {
        autoRevalidatedRef.current = null;
        setValidatingCustomDomain(input.primaryDomain);
        validateCustomDomain(input);
    };

    // One automatic follow-up per manual click; the ref is the cap.
    useEffect(() => {
        const message: string | undefined = validatedCustomDomainData?.validationErrorMessage;
        if (!message || !validatingCustomDomain) {
            return;
        }
        if (shouldAutoRevalidate(validatedCustomDomainData) && autoRevalidatedRef.current !== validatingCustomDomain) {
            autoRevalidatedRef.current = validatingCustomDomain;
            toaster.notify("The certificate order was replaced - validating again automatically.");
            validateCustomDomain({ primaryDomain: validatingCustomDomain });
        } else {
            // Genuine failure, or a second reissue in a row.
            toaster.danger(message);
        }
    }, [validatedCustomDomainData, validatingCustomDomain, validateCustomDomain]);

    const [requestedCustomDomain, setRequestedCustomDomain] = useState<string | null>(null);
    const requestCustomDomain = (input: TundraStorefrontCreateCustomDomainInput): void => {
        setRequestedCustomDomain(input.primaryDomain);
        createCustomDomain(input);
    };

    const [hasPolled, setHasPolled] = useState<boolean>(false);
    useEffect(() => {
        setHasPolled(false);
    }, [validatedCustomDomainData, createdCustomDomainData]);

    const renderedStorefront = resolveRenderedStorefront(
        hasPolled, getStorefrontData, validatedCustomDomainData, createdCustomDomainData
    );
    const hasDeployingCert: boolean = !!renderedStorefront?.domains?.some(
        (domain) => domain.alias.lastCertificateRenewal === "DEPLOYING"
    );

    const [lastRefreshedAt, setLastRefreshedAt] = useState<Date>(new Date());
    useEffect(() => {
        setLastRefreshedAt(new Date());
    }, [renderedStorefront]);

    const pollInFlightRef = useRef<boolean>(false);
    useEffect(() => {
        if (!hasDeployingCert) {
            return;
        }
        const intervalId = setInterval(() => {
            handlePollTick(pollInFlightRef, refetchStorefront, () => {
                setHasPolled(true);
                // Apollo hands back the same object when a poll returns unchanged data, so the
                // effect above would not fire and the subtext would go stale mid-deploy.
                setLastRefreshedAt(new Date());
            });
        }, 15000);
        return () => clearInterval(intervalId);
    }, [hasDeployingCert, refetchStorefront]);

    useEffect(() => {
        if (createStorefrontCalled && !createStorefrontLoading && !createdStorefrontData) {
            toaster.danger("Failed to create storefront - check inputs and try again.");
        }
    }, [createStorefrontCalled, createStorefrontLoading, createdStorefrontData]);

    useEffect(() => {
        if (!createdCustomDomainData || !requestedCustomDomain) {
            return;
        }
        const added: boolean = createdCustomDomainData.domains.some(
            (domain) => domain.alias.alias === requestedCustomDomain
        );
        if (!added) {
            toaster.danger("Failed to create custom domain - check logs for more information.");
        }
    }, [createdCustomDomainData, requestedCustomDomain]);

    const deleteStorefront = async (domain: string): Promise<void> => {
        if (!window.confirm("Are you sure you want to delete this storefront? It will take some time to recreate it.")) {
            return; // Don't delete if the user selects cancel.
        }
        setDeletingStorefront(true);
        if (await deleteTundraStorefront(domain)) {
            window.location.reload(); // Let's just refresh this
        } else {
            toaster.danger("Failed to delete storefront - check logs for more information.");
        }
        setDeletingStorefront(false);
    };

    const renderStorefrontView = (storefront: TundraStorefront): JSX.Element => {
        return <StoreTundraAdminGetView
            storefront={storefront}
            createCustomDomain={requestCustomDomain}
            createCustomDomainLoading={createCustomDomainLoading}
            createCustomDomainCalled={createCustomDomainCalled}
            validateCustomDomain={requestValidateCustomDomain}
            validateCustomDomainLoading={validateCustomDomainLoading}
            validatingCustomDomain={validatingCustomDomain}
            deleteStorefront={deleteStorefront}
            lastRefreshedAt={lastRefreshedAt}
        />;
    };

    const renderCreateStorefrontView = (): JSX.Element => {
        return <StoreTundraAdminCreateView
            createStorefront={createStorefront}
            createStorefrontLoading={createStorefrontLoading}
            storeId={store!.id}
        />;
    };

    // Loading is also true on poll refetches; only spin before the first load.
    if ((getStorefrontLoading && !getStorefrontData) || deletingStorefront) {
        return <CenteredSpinner />;
    }

    if (renderedStorefront) {
        return renderStorefrontView(renderedStorefront);
    }

    // Newly created storefront
    if (createdStorefrontData) {
        return renderStorefrontView(createdStorefrontData);
    }

    // Create storefront view
    return renderCreateStorefrontView();
};
